import { Card, Col, Flex, Form, Row, Select } from "antd";
import "./select-procedure.css";
import "./select-procedure-treatment.css";
import { useQuote } from "../QuoteProvider";
import { Suspense, lazy, useEffect, useRef, useState } from "react";
import QuoteSummary from "../QuoteSummary";
import QuoteButton from "../components/QuoteButton/QuoteButton";
import TextArea from "antd/es/input/TextArea";
import QuoteProceduresListFocused from "../components/QuoteProceduresList/QuoteProceduresListFocused";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "../MobileBreakpointContext";
import {
  oncePerSession,
  trackQuoteFocusModeEntered,
  trackQuoteProcedureCtaClicked,
  trackQuoteProcedureCtaDisabledClicked,
} from "../../../utils/quoteAnalytics";
import { CampaignFocus } from "../hooks/useCampaignFocus";
import { procedureDisplayName } from "../../../utils/procedureDisplayName";
import { Procedure } from "../../../types/procedures";
import { ErrorBoundary } from "../../ErrorBoundary";
import ProcedureStoryLoading, {
  ProcedureStoryUnavailable,
} from "../components/ProcedureStory/ProcedureStoryLoading";

// Lazy for the same reason as in StepSelectProcedureV2 — the story surface
// must never land in the funnel's eager entry chunk.
const ProcedureStory = lazy(
  () => import("../components/ProcedureStory/ProcedureStory"),
);

type StepSelectProcedureV2TreatmentProps = {
  next: () => void;
  focus: CampaignFocus;
};

/**
 * Focused campaign mode (the concluded procedure-focus-v1 "focused" arm,
 * now served to 100% of campaign-eligible traffic). Mounts for every
 * campaign-eligible session — see StepSelectProcedure. Differences vs
 * StepSelectProcedureV2 (the default step for non-campaign traffic):
 *
 * - The focus procedures lead (pinned + accented on top); the rest of the
 *   catalog renders below them by default, under an "All procedures" divider.
 * - No "Help me choose" card (it clears selections) and no type filters.
 * - Single-procedure focus is auto-selected so Continue is live on arrival.
 * - Tailored title + "Quote for:" chip + reassurance line.
 */
const StepSelectProcedureV2Treatment = ({
  next,
  focus,
}: StepSelectProcedureV2TreatmentProps) => {
  // @ts-ignore - useTranslation with namespace parameter
  const { t } = useTranslation("quote");
  // @ts-ignore
  const { t: tProcedures, i18n } = useTranslation("procedures");
  const [storyProcedure, setStoryProcedure] = useState<Procedure | null>(null);
  const isMobile = useIsMobile();
  const quoteData = useQuote();
  // Disabled-CTA feedback (2026-09-13 usability fix) — see StepSelectProcedureV2
  // for the underlying data (73 disabled taps since 2026-08-01, 28/28
  // abandonment when zero procedures were selected).
  const [showDisabledHint, setShowDisabledHint] = useState(false);
  const proceduresListRef = useRef<HTMLDivElement>(null);

  const surface = isMobile ? "mobile" : "desktop";

  const singleFocusProcedure =
    focus.focusType === "single" ? focus.focusProcedures[0] : null;

  // Human label for the title/chip: the procedure name for a single focus,
  // the family name for a one-type campaign. Multi-type / id-grab-bag
  // campaigns get no clean label — fall back to the default title, no chip.
  const focusLabel = singleFocusProcedure
    ? procedureDisplayName(singleFocusProcedure, {
        language: i18n?.language,
        t: tProcedures,
      })
    : focus.matchedTypes.length === 1
      ? tProcedures(`types.${focus.matchedTypes[0].name}`, {
          defaultValue: focus.matchedTypes[0].name,
        })
      : null;

  // Mirror QuoteFiltersForm's mobile behavior (the control's filter form
  // auto-widens to all genders on mobile) so the two arms see the same
  // catalog. Desktop keeps the FEMALE default — identical to the control's
  // default state — and gets the same gender select as the control (below).
  useEffect(() => {
    if (isMobile) {
      quoteData.setGender(null);
    }
    // eslint-disable-next-line
  }, [isMobile]);

  // Once per mount: entered-event (deduped per session — back-navigation
  // remounts the step and must not inflate the entry counter) and the
  // single-focus auto-select so Continue is live on arrival (one-tap
  // confirm). Mount-only on purpose: if the visitor deselects, we never
  // force the selection back.
  useEffect(() => {
    if (oncePerSession("focus-mode-entered")) {
      trackQuoteFocusModeEntered({
        focusType: focus.focusType,
        focusIds: Array.from(focus.focusIds),
        focusTypesParam: focus.focusTypesParam,
        surface,
      });
    }
    if (singleFocusProcedure && quoteData.selectedProcedures.length === 0) {
      quoteData.addSelectedProcedure(singleFocusProcedure);
    }
    // eslint-disable-next-line
  }, []);

  const selectedCount = quoteData.selectedProcedures.length;
  const selectedProcedureIds = quoteData.selectedProcedures.map((p) => p.id);
  const hasSelection = selectedCount > 0;
  const ctaCopy =
    selectedCount > 0
      // Real i18next plurals (`_one` / `_other`) — see the same note in
      // StepSelectProcedureV2.
      ? t("procedures.continueWithSelected", { count: selectedCount })
      : t("procedures.selectToContinue", {
          defaultValue: "Select a procedure to continue",
        });

  const handleDisabledCtaClick = () => {
    trackQuoteProcedureCtaDisabledClicked();
    setShowDisabledHint(true);
    proceduresListRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  // Same options as the control's QuoteFiltersForm gender select.
  const genderOptions = [
    { label: t("procedures.female"), value: "FEMALE" },
    { label: t("procedures.male"), value: "MALE" },
    { label: t("procedures.allGenders"), value: null },
  ];

  return (
    <div className="select-procedureV2 select-procedureV2--focused">
      {storyProcedure && (
        <ErrorBoundary
          fallback={
            <ProcedureStoryUnavailable
              onClose={() => setStoryProcedure(null)}
            />
          }
        >
          <Suspense
            fallback={
              <ProcedureStoryLoading
                onClose={() => setStoryProcedure(null)}
              />
            }
          >
            <ProcedureStory
              procedure={storyProcedure}
              onClose={() => setStoryProcedure(null)}
            />
          </Suspense>
        </ErrorBoundary>
      )}
      <Row>
        {/* Empty gutter column — the Row's spans are 4 + 12 + 8; it used to
            host the info Modal (a portal, so it never had size of its own) and
            still has to hold the desktop layout's left gutter open. */}
        <Col span={isMobile ? 24 : 4} />
        <Col span={isMobile ? 24 : 12} className="select-procedureV2-main-col">
          <div className="select-procedureV2-main-header">
            <div className="select-procedures-title">
              {focusLabel
                ? t("procedures.focusTitle", {
                    focus: focusLabel,
                    defaultValue: `Let's build your ${focusLabel} quote`,
                  })
                : t("procedures.selectYourProcedures")}
            </div>
            {focusLabel && (
              <span className="focus-context-chip">
                {t("procedures.focusContextChip", {
                  focus: focusLabel,
                  defaultValue: `Quote for: ${focusLabel}`,
                })}
              </span>
            )}
            <div className="focus-reassurance">
              {focus.focusType === "single"
                ? t("procedures.focusReassure", {
                    defaultValue:
                      "Not sure which is right? Our team will help — just continue.",
                  })
                : t("procedures.focusReassureFamily", {
                    defaultValue:
                      "Not sure which is right? Pick the closest one — our team will fine-tune it with you.",
                  })}
            </div>
            {!isMobile && (
              <Flex gap="small" wrap="wrap" style={{ margin: "12px 0 0" }}>
                <Select
                  style={{ width: "48%" }}
                  options={genderOptions}
                  defaultValue={quoteData.gender}
                  onChange={(value: any) => {
                    quoteData.setGender(value);
                  }}
                />
              </Flex>
            )}
          </div>
          <div className="procedures-list-container" ref={proceduresListRef}>
            <div className="procedures-list-mobile-padding">
              <QuoteProceduresListFocused
                focusProcedures={focus.focusProcedures}
                focusIds={focus.focusIds}
                setStoryProcedure={setStoryProcedure}
              />
              <div className="procedures-list-bottom-spacer" />
            </div>
          </div>
        </Col>
        <Col span={isMobile ? 24 : 8} className="select-procedureV2-side-col">
          {!isMobile && <QuoteSummary />}
          {!quoteData.selectedSurgeon && !isMobile && (
            <Card className="describe-detail-card">
              <Form layout="vertical">
                <Form.Item label={t("procedures.describeInDetail")}>
                  <TextArea
                    value={quoteData.procedureDescription}
                    onChange={(
                      event: React.ChangeEvent<HTMLTextAreaElement>,
                    ) => {
                      quoteData.setProcedureDescription(event.target.value);
                    }}
                  ></TextArea>
                </Form.Item>
              </Form>
            </Card>
          )}
          <div className="quote-button-mobile-container">
            {showDisabledHint && !hasSelection && (
              <div className="cta-disabled-hint" role="status">
                {t("procedures.ctaDisabledHint", {
                  defaultValue: "Select at least one procedure to continue",
                })}
              </div>
            )}
            <QuoteButton
              disabled={!hasSelection}
              onDisabledClick={handleDisabledCtaClick}
              onClick={() => {
                trackQuoteProcedureCtaClicked(
                  selectedCount,
                  selectedProcedureIds,
                  selectedProcedureIds.some((id) => focus.focusIds.has(id)),
                );
                next();
              }}
            >
              {ctaCopy}
            </QuoteButton>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default StepSelectProcedureV2Treatment;
